import { HolderOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { memo, useMemo } from 'react';

import type { ModelLead } from '@app/api/Leads/types';

import { buildLeadSortableId } from '../../../../utils';
import { LeadsFunnelLeadCard } from '../../LeadsFunnelLeadCard';

import type { CSSProperties, FC, MouseEvent } from 'react';

interface LeadsFunnelSortableLeadCardProps {
  lead: ModelLead;
  canShowContacts: boolean;
  funnelId: number | null;
  stageId: number;
}

// Stable module-level handler: no closure deps, so useMemo([..., onClick]) is
// not needed and the dragHandle memo is not invalidated by a new function ref.
const handleDragHandleClick = (event: MouseEvent<HTMLButtonElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const LeadsFunnelSortableLeadCardBase: FC<LeadsFunnelSortableLeadCardProps> = ({
  lead,
  canShowContacts,
  funnelId,
  stageId,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: buildLeadSortableId(lead.Id),
    data: { stageId },
  });

  // At rest: transform=null, transition=null — primitives that don't change
  // across renders. useMemo keeps the same object ref → LeadsFunnelLeadCard
  // memo() receives a stable rootStyle and does not re-render the card body.
  const style = useMemo<CSSProperties>(
    () =>
      isDragging
        ? { transition }
        : { transform: CSS.Translate.toString(transform), transition },
    [isDragging, transform, transition]
  );

  // dnd-kit returns stable refs for attributes, listeners, and
  // setActivatorNodeRef at rest. useMemo keeps the same JSX element ref →
  // LeadsFunnelLeadCard memo() sees a stable dragHandle prop at rest.
  const dragHandle = useMemo(
    () => (
      <button
        type="button"
        className="LeadsFunnelLeadCard__drag-handle"
        ref={setActivatorNodeRef}
        aria-label="Перетащить лид на другой этап"
        onClick={handleDragHandleClick}
        {...attributes}
        {...listeners}
      >
        <HolderOutlined />
      </button>
    ),
    [attributes, listeners, setActivatorNodeRef]
  );

  return (
    <LeadsFunnelLeadCard
      lead={lead}
      canShowContacts={canShowContacts}
      funnelId={funnelId}
      stageId={stageId}
      rootRef={setNodeRef}
      rootStyle={style}
      isDragging={isDragging}
      dragHandle={dragHandle}
    />
  );
};

export const LeadsFunnelSortableLeadCard = memo(
  LeadsFunnelSortableLeadCardBase
);
